import { ReactComponent as SearchGlyph } from '@/assets/images/svg/glyphs/search.svg';
import AppCard from '@/components/AppCard';
import { PrimaryDefaultButton } from '@/components/buttons/primary';
import FrontPinkButton from '@/components/buttons/primary/FrontPinkButton';
import SecondaryDefaultButton from '@/components/buttons/secondary';
import Carousel from '@/components/carousels/dots';
import FilterBlock from '@/components/FilterBlock';
import InputFieldElement from '@/components/forms/InputFieldElement';
import Image from '@/components/Image';
import { Container } from '@/components/layout/Container';
import Link from '@/components/Link';
import { PRIMARY_NAV_HEIGHT } from '@/components/navigation/PrimaryNavigation';
import { INTEGRATIONS_DOCUMENTATION, INTEGRATIONS_PARTNER_INFO } from '@/constants/routes';
import { CUSTOMER_COUNT } from '@/constants/strings';
import { GlobalContext } from '@/context';
import { IntegrationsHomepageQuery, Maybe } from '@/graphql';
import IntegrationsHomepageQueryDocument from '@/graphql/queries/IntegrationsHomepage.gql';
import gtm from '@/lib/gtm';
import useBreakpoint from '@/lib/hooks/useBreakpoint';
import { getStaticPageProps } from '@/lib/next';
import { getSingle, maybeNotSet, notEmpty } from '@/lib/typescript-helpers';
import { COLORS } from '@/styles/color';
import { applyBreakpointNegativeMargins, mediaBreakpointUp, paddingX, paddingY } from '@/styles/layout/utils';
import { FONT_WEIGHTS, TEXT_SIZES } from '@/styles/typography';
import { customTextSize, remCalc } from '@/styles/typography/utils';
import MasterTemplate from '@/templates/MasterTemplate';
import { useDebounce } from '@uidotdev/usehooks';
import dayjs from 'dayjs';
import { capitalize } from 'lodash';
import { ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { ArrayParam, StringParam, useQueryParam } from 'use-query-params';

// #region - Components

const PageWrapper = styled.div`
  padding-top: ${remCalc(PRIMARY_NAV_HEIGHT)};
`;

const Header = styled.header`
  border-radius: ${remCalc(10)};
  background-color: ${COLORS.GRAY.HIGHLIGHT};

  display: flex;
  flex-direction: column;
  row-gap: ${remCalc(24)};

  margin-bottom: ${remCalc(40)};

  ${mediaBreakpointUp('desktop')} {
    flex-direction: row;
    align-items: center;
  }
`;

const HeaderLeftColumn = styled.div`
  padding: ${remCalc(24, 24, 0)};

  ${mediaBreakpointUp('desktop')} {
    padding: ${remCalc(70, 24, 74, 104)};
    flex: 1 0 50%;
  }

  h1 {
    ${customTextSize(40, 42)}
    font-weight: ${FONT_WEIGHTS.SEMIBOLD};
    text-align: center;

    ${mediaBreakpointUp('desktop')} {
      text-align: left;
      ${customTextSize(56, 53)}
    }

    ${mediaBreakpointUp('desktopWide')} {
      ${customTextSize(72, 70)}
    }

    small {
      display: block;
      ${customTextSize(16, 26)}
      font-weight: ${FONT_WEIGHTS.NORMAL};
      margin-bottom: ${remCalc(16)};
    }
  }
`;

const HeaderRightColumn = styled.div`
  padding: ${remCalc(0, 24, 24, 24)};
  width: 100%;

  ${mediaBreakpointUp('desktop')} {
    padding: ${remCalc(70, 104, 74, 24)};
    flex: 1 0 50%;
    height: 100%;
  }
`;

const HeaderImage = styled.div`
  img {
    width: auto;
    max-height: 250px;
    margin: 0 auto;
  }

  ${mediaBreakpointUp('desktop')} {
    img {
      max-height: none;
    }
  }
`;

const VisuallyHidden = styled.span`
  visibility: hidden;
  opacity: 0;
  position: absolute;
  top: -9999px;
  left: -9999px;
`;

const StyledFilterBlock = styled(FilterBlock)`
  position: sticky;
  top: ${remCalc(PRIMARY_NAV_HEIGHT + 32)};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 100%;
  grid-template-rows: auto auto;
  grid-gap: ${remCalc(32)};

  ${mediaBreakpointUp('desktop')} {
    grid-template-columns: ${remCalc(288)} auto;
    grid-template-rows: auto;
  }
`;

const AppGrid = styled.div`
  display: grid;
  grid-template-columns: auto;
  grid-auto-rows: auto;
  grid-gap: ${remCalc(18)};

  ${mediaBreakpointUp('desktop')} {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const SearchInputWrapper = styled.div`
  position: relative;
`;

const SearchInput = styled(InputFieldElement)`
  appearance: none;
  padding-left: ${remCalc(40)};
  margin-bottom: ${remCalc(32)};

  &::-ms-clear,
  &::-ms-reveal {
    display: none;
    width: 0;
    height: 0;
  }
  &::-webkit-search-decoration,
  &::-webkit-search-cancel-button,
  &::-webkit-search-results-button,
  &::-webkit-search-results-decoration {
    display: none;
  }
`;

const StyledSearchGlyph = styled(SearchGlyph)`
  position: absolute;
  top: ${remCalc(10)};
  left: ${remCalc(10)};
  width: ${remCalc(20)};
  height: ${remCalc(20)};
`;

const Heading = styled.h2`
  ${customTextSize(24, 30)}

  small {
    display: block;
    ${customTextSize(16, 30)}
  }
`;

const AppSection = styled.div`
  ${mediaBreakpointUp('desktop')} {
    margin-bottom: ${remCalc(40)};
  }
`;

const AppSectionHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${remCalc(24)};

  ${mediaBreakpointUp('desktop')} {
    justify-content: space-between;
  }
`;

const StyledCarousel = styled(Carousel)`
  ${applyBreakpointNegativeMargins}
`;

const RelatedList = styled.div`
  display: flex;
  flex-direction: row;
  column-gap: ${remCalc(16)};

  > div {
    display: block;
    flex: 1 1 100%;
  }
`;

const ShowAllButtton = styled.button`
  font-weight: ${FONT_WEIGHTS.SEMIBOLD};
  color: ${COLORS.BLACK};
`;

const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  ${paddingY(60)}
`;

const NotFoundWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  row-gap: ${remCalc(16)};
`;

const CalloutBlock = styled.div`
  ${paddingX(32)};
  ${paddingY(32)};
  margin-bottom: ${remCalc(40)};

  background: linear-gradient(209deg, #001b38 23.25%, #006eb2 161.08%, #001b38);
  border-radius: ${remCalc(10)};

  display: flex;
  flex-direction: column;
  align-items: center;
  row-gap: 2rem;

  ${mediaBreakpointUp('desktop')} {
    margin-top: ${remCalc(100)};
  }

  ${mediaBreakpointUp('desktopWide')} {
    flex-direction: row;
    row-gap: 0;
    column-gap: ${remCalc(104)};
    ${paddingX(104)};
    ${paddingY(42)};
  }
`;

const Left = styled.div`
  flex-grow: 1;
`;

const Right = styled.div`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  gap: ${remCalc(16)};

  ${mediaBreakpointUp('tablet')} {
    flex-direction: row;
  }
`;

const Text = styled.p`
  ${TEXT_SIZES[38]};

  font-weight: ${FONT_WEIGHTS.MEDIUM};
  color: ${COLORS.WHITE};
  text-align: center;

  ${mediaBreakpointUp('desktopWide')} {
    text-align: left;
  }
`;

const StyledPrimaryDefaultButton = styled(PrimaryDefaultButton)`
  --button-color: ${COLORS.WHITE};
  --button-text-color: ${COLORS.BLACK};
  --button-color-hover: ${COLORS.BLUE.LOWLIGHT};
  --button-text-color-hover: ${COLORS.WHITE};

  width: 100%;

  ${mediaBreakpointUp('tablet')} {
    width: auto;
  }
`;

const StyledSecondaryDefaultButton = styled(SecondaryDefaultButton)`
  --button-color: ${COLORS.WHITE};
  --button-text-color: ${COLORS.WHITE};
  --button-color-hover: ${COLORS.BLUE.LOWLIGHT};
  --button-text-color-hover: ${COLORS.WHITE};

  width: 100%;

  ${mediaBreakpointUp('tablet')} {
    width: auto;
  }
`;

const LargeCalloutBlock = styled.div`
  padding: ${remCalc(32)};
  margin-bottom: ${remCalc(40)};

  border-radius: ${remCalc(10)};

  display: flex;
  flex-direction: column;
  align-items: center;
  row-gap: ${remCalc(60)};

  ${mediaBreakpointUp('tablet')} {
    padding: ${remCalc(50)};
  }

  ${mediaBreakpointUp('desktopWide')} {
    ${paddingX(40)};
    ${paddingY(60)};
  }
  p {
    ${customTextSize(40, 38)};
    font-weight: ${FONT_WEIGHTS.SEMIBOLD};
    color: ${COLORS.BLACK};
    text-align: center;
    max-width: ${remCalc(1100)};
    margin: 0 auto;
    ${mediaBreakpointUp('desktopWide')} {
      ${customTextSize(44, 42)};
    }
  }

  ${StyledPrimaryDefaultButton} {
    --button-color-hover: ${COLORS.BLACK};
  }
`;

const CTASection = styled.div`
  ${paddingY(40)};
`;

// #endregion - Components

const ANALYTICS_CATEGORY = 'Integrations';

// Helper function to shuffle an array randomly (only after client-side hydration)
const shuffleArray = <T,>(array: T[], shouldShuffle: boolean): T[] => {
  // Skip shuffling during SSR and initial render to avoid hydration mismatch
  if (!shouldShuffle) {
    return array;
  }

  const shuffled = [...array];

  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));

    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const exploreFilters = [
  {
    id: 'all',
    slug: 'all',
    title: 'All',
  },
  {
    id: 'featured',
    slug: 'featured',
    title: 'Featured',
  },
  {
    id: 'new',
    slug: 'new',
    title: 'New',
  },
  {
    id: 'channels',
    slug: 'channels',
    title: 'Channels',
  },
];

const IntegrationsIndex: PageTemplate<IntegrationsHomepageQuery> = ({ entry, entries, categories, plans }) => {
  const { toggleGetStartedModal } = useContext(GlobalContext);
  const [, activeBreakpoints] = useBreakpoint();
  const contentColumnRef = useRef<HTMLDivElement>(null);
  const [isMounted, setIsMounted] = useState(false);

  const [searchTerms, setSearchTerms] = useQueryParam('search', StringParam, { updateType: 'replaceIn' });
  const [selectedCategory, setSelectedCategory] = useQueryParam('category', StringParam, { updateType: 'replaceIn' });
  const [selectedPlans, setSelectedPlans] = useQueryParam('plan', ArrayParam, { updateType: 'replaceIn' });

  const getFeaturedApps = useCallback(() => {
    const featuredId = categories?.find((category) => category?.title?.toLowerCase() === 'featured')?.id;

    let apps = [...(entries ?? [])].filter(notEmpty);

    if (featuredId) {
      return apps.filter((app) => {
        // Assemble an array of all category IDs that this app has
        const appCategories = [...(app.integrationCategories?.map((category) => category?.id) ?? [])].filter(notEmpty);

        // Check if there are any matches
        return appCategories.includes(featuredId);
      });
    }

    return [];
  }, [entries]);

  const getNewApps = useCallback(() => {
    let apps = [...(entries ?? [])].filter(notEmpty);

    return apps
      .sort((app1, app2) => {
        return dayjs(app1.postDate).isBefore(app2.postDate) ? 1 : -1;
      })
      .slice(0, 3 * 5);
  }, [entries]);

  const getChannelApps = useCallback(() => {
    const channelId = categories?.find((category) => category?.title?.toLowerCase()?.startsWith('channel'))?.id;

    let apps = [...(entries ?? [])].filter(notEmpty);

    if (channelId) {
      return apps.filter((app) => {
        // Assemble an array of all category IDs that this app has
        const appCategories = [
          app.integrationPrimaryCategory?.[0]?.id,
          ...(app.integrationCategories?.map((category) => category?.id) ?? []),
        ].filter(notEmpty);

        // Check if there are any matches
        return appCategories.includes(channelId);
      });
    }

    return [];
  }, [entries]);

  const filterCategories = useMemo(() => {
    return (
      categories
        ?.map((category) => {
          if (!category || category.slug === 'featured' || category.slug?.startsWith('channel')) return null;

          return {
            id: category.id!,
            slug: category.slug!,
            title: category.title!,
          };
        })
        .filter(notEmpty) ?? []
    );
  }, [categories]);

  const filterPlans = useMemo(() => {
    return (
      plans
        ?.map((plan) => {
          if (!plan) return null;

          return {
            id: plan.id!,
            slug: plan.slug!,
            title: plan.title!,
          };
        })
        .filter(notEmpty) ?? []
    );
  }, [plans]);

  const filteredApps = useMemo(() => {
    if (!entries) return [];

    let apps = [...entries].filter(notEmpty);

    if (selectedCategory === 'featured') {
      apps = getFeaturedApps();
    } else if (selectedCategory === 'new') {
      apps = getNewApps();
    } else if (selectedCategory === 'channels') {
      apps = getChannelApps();
    } else if (selectedCategory && selectedCategory !== 'all') {
      apps = apps.filter((app) => {
        // Assemble an array of all category IDs that this app has
        const appCategories = [
          app.integrationPrimaryCategory?.[0]?.slug,
          ...(app.integrationCategories?.map((category) => category?.slug) ?? []),
        ].filter(notEmpty);

        // Check if there are any matches
        return appCategories.includes(selectedCategory);
      });
    }

    if (selectedPlans?.length) {
      apps = apps.filter((app) => {
        // Assemble an array of all category IDs that this app has
        const planCategories = [...(app.associatedPlans?.map((plan) => plan?.slug) ?? [])].filter(notEmpty);

        // Check if there are any matches
        return planCategories.some((plan) => selectedPlans.includes(plan));
      });
    }

    if (searchTerms) {
      const appEntryContainsSearchTerms = (app?: {
        title?: Maybe<string>;
        description?: Maybe<string>;
        summary?: Maybe<string>;
      }): boolean => {
        if (!app) return false;

        const simplifyString = (content?: string | null) => {
          if (!content) return '';

          return content.toLowerCase().replace(/[^a-z]/gi, '');
        };

        const simplifiedSearchTerms = simplifyString(searchTerms);

        return (
          simplifyString(app.title).includes(simplifiedSearchTerms) ||
          simplifyString(app.description).includes(simplifiedSearchTerms) ||
          simplifyString(app.summary).includes(simplifiedSearchTerms) ||
          false
        );
      };

      apps = apps.filter(appEntryContainsSearchTerms);
    }

    return apps;
  }, [entries, searchTerms, selectedCategory, selectedPlans]);

  const filterSelections = useMemo(() => {
    let filterIds: string[] = [];

    if (selectedCategory) {
      if (exploreFilters.some((filter) => filter.slug === selectedCategory)) {
        filterIds.push(selectedCategory);
      } else {
        const categoryMatch = filterCategories.find((category) => category.slug === selectedCategory);

        if (categoryMatch) {
          filterIds.push(categoryMatch.id);
        }
      }
    }

    if (selectedPlans?.length) {
      selectedPlans.filter(notEmpty).forEach((slug) => {
        const match = filterPlans.find((plan) => plan.slug === slug);

        if (match) {
          filterIds.push(match.id);
        }
      });
    }

    return filterIds;
  }, [filterCategories, filterPlans, selectedPlans, selectedCategory]);

  const selectedAppsTitle = useMemo(() => {
    if (!selectedCategory) return null;

    const title =
      categories?.find((category) => category?.slug === selectedCategory)?.title ?? capitalize(selectedCategory);

    if (title === 'Channels') {
      return title;
    }

    return `${title} apps`;
  }, [selectedCategory]);

  const selectedPlansTitle = useMemo(() => {
    if (!selectedPlans) return null;

    const names = selectedPlans.map((planSlug) => plans?.find((plan) => plan?.slug === planSlug)?.title);

    if (names.length === 1) {
      return names[0];
    }

    if (names.length === 2) {
      return `${names[0]} or ${names[1]}`;
    }

    const final = names.pop();

    return `${names.join(', ')}, or ${final}`;
  }, [selectedPlans]);

  const showHome = useMemo(() => {
    return !searchTerms && !selectedCategory && !selectedPlans?.length;
  }, [searchTerms, selectedCategory, selectedPlans]);

  const homeApps = useMemo(() => {
    const categories = [
      ...exploreFilters.slice(1).map((filter) => {
        let apps: typeof entries | ReactNode = [];

        if (filter.id === 'featured') {
          apps = shuffleArray(getFeaturedApps(), isMounted).slice(0, 3);
        } else if (filter.id === 'new') {
          apps = getNewApps().slice(0, 3);
        } else if (filter.id === 'channels') {
          apps = shuffleArray(getChannelApps(), isMounted).slice(0, 3);
        }

        return {
          id: filter.id,
          title: filter.title,
          apps,
        };
      }),
      ...(entry?.featuredIntegrationCategories?.map((category) => {
        if (!category) return null;

        const apps = shuffleArray(
          entries
            ?.filter((app) => {
              if (!app) return false;

              // Assemble an array of all category IDs that this app has
              const appCategories = [
                app.integrationPrimaryCategory?.[0]?.id,
                ...(app.integrationCategories?.map((category) => category?.id) ?? []),
              ].filter(notEmpty);

              // Check if there are any matches
              return appCategories.includes(category?.category?.[0]?.id!);
            })
            .filter(notEmpty) ?? [],
          isMounted
        ).slice(0, 3);

        return {
          id: category?.category?.[0]?.slug,
          title: category?.category?.[0]?.title,
          apps,
        };
      }) ?? []),
    ].filter(notEmpty);

    categories.forEach((category) => {
      category.apps = (category.apps as typeof entries)?.map((app) => {
        if (!app) return null;

        const logo = getSingle(app?.logo);

        return (
          <div key={app.id}>
            <Link href={app?.url ?? '#'} key={app.id}>
              <a
                onClick={() => {
                  gtm.track('click', {
                    location: ANALYTICS_CATEGORY,
                    element_type: 'link',
                    element_label: maybeNotSet(app.title),
                  });
                }}
              >
                <AppCard
                  layout="large"
                  title={app.title!}
                  text={app.integrationPrimaryCategory?.[0]?.title!}
                  image={
                    logo ? (
                      <Image src={logo.url!} width={64} height={64} sizes="64px" alt={logo.altText ?? ''} />
                    ) : undefined
                  }
                />
              </a>
            </Link>
          </div>
        );
      }) ?? [];
    });

    return categories;
  }, [isMounted]);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if ((searchTerms || selectedPlans?.length) && !selectedCategory) {
      setSelectedCategory('all');
    }

    // Scroll!

    const rect = contentColumnRef.current?.getBoundingClientRect();

    if (!rect) return;

    const top = rect.top + window.scrollY - PRIMARY_NAV_HEIGHT - 32;

    if (window.scrollY > top) {
      window.scrollTo({ top });
    }
  }, [selectedCategory, selectedPlans, searchTerms]);

  const debouncedSearchTerm = useDebounce(searchTerms, 1000);

  useEffect(() => {
    if (!debouncedSearchTerm) return;

    gtm.track('search', {
      search_terms: debouncedSearchTerm,
    });
  }, [debouncedSearchTerm]);

  return (
    <PageWrapper>
      <Container>
        <Header>
          <HeaderLeftColumn>
            <h1>
              <small>Integrations</small>
              <VisuallyHidden>: </VisuallyHidden>
              <span>Manage all of your favorite apps, right within Front</span>
            </h1>
          </HeaderLeftColumn>

          <HeaderRightColumn>
            <HeaderImage>
              <Image src="/assets/pages/integrations/integrations-hero.png" width={1068} height={553} alt="" />
            </HeaderImage>
          </HeaderRightColumn>
        </Header>

        <Grid>
          <div>
            <StyledFilterBlock
              selections={filterSelections}
              collapsible={false}
              categories={[
                {
                  title: 'Explore',
                  slug: 'explore',
                  filters: exploreFilters,
                },
                {
                  title: 'Categories',
                  slug: 'categories',
                  filters: filterCategories,
                },
                {
                  title: 'Plans',
                  slug: 'plans',
                  filters: filterPlans,
                },
              ]}
              onFilterToggle={(category, filter, selected) => {
                switch (category.slug) {
                  case 'explore':
                  case 'categories':
                    setSelectedCategory(selected ? filter.slug : null);
                    break;

                  case 'plans':
                    if (selected) {
                      setSelectedPlans([...(selectedPlans ?? []), filter.slug]);
                    } else {
                      setSelectedPlans((selectedPlans ?? []).filter((slug) => slug !== filter.slug));
                    }

                    break;
                }

                gtm.track(selected ? 'checked_filter' : 'unchecked_filter', {
                  location: ANALYTICS_CATEGORY,
                  category_title: maybeNotSet(category.title),
                  filter_title: maybeNotSet(filter.title),
                });
              }}
            />
          </div>

          <div ref={contentColumnRef}>
            <SearchInputWrapper>
              <SearchInput
                type="search"
                placeholder="Search for an app..."
                value={searchTerms ?? ''}
                onInput={({ target }) => setSearchTerms((target as HTMLInputElement).value || null)}
              />
              <StyledSearchGlyph />
            </SearchInputWrapper>

            {showHome ? (
              <div>
                {homeApps.map((category) => (
                  <AppSection key={category.id}>
                    <AppSectionHeader>
                      <Heading>{category.title}</Heading>

                      <ShowAllButtton
                        onClick={() => {
                          setSelectedCategory(category.id!);

                          gtm.track('click', {
                            element_type: 'link',
                            element_label: `See all | ${category.title}`,
                            location: ANALYTICS_CATEGORY,
                          });
                        }}
                      >
                        See all
                      </ShowAllButtton>
                    </AppSectionHeader>

                    {!activeBreakpoints.includes('desktop') ? (
                      <StyledCarousel items={(category.apps as ReactNode[]) ?? []} />
                    ) : (
                      <RelatedList>{(category.apps as ReactNode[])?.map((item) => item) ?? []}</RelatedList>
                    )}
                  </AppSection>
                ))}
              </div>
            ) : (
              <div>
                <AppSectionHeader>
                  <Heading>
                    {selectedAppsTitle}
                    {selectedPlans && selectedPlans.length > 0 ? (
                      <small>
                        Available on the {selectedPlansTitle} {selectedPlans.length > 1 ? 'plans' : 'plan'}
                      </small>
                    ) : null}
                  </Heading>
                </AppSectionHeader>

                {filteredApps.length > 0 && (
                  <AppGrid>
                    {filteredApps?.map((entry) => {
                      if (!entry) return null;

                      const logo = getSingle(entry?.logo);

                      return (
                        <Link href={entry?.url ?? '#'} key={entry.id}>
                          <a
                            onClick={() => {
                              gtm.track('click', {
                                element_label: `Integration | ${maybeNotSet(entry.title)}`,
                                location: ANALYTICS_CATEGORY,
                                element_type: 'link',
                              });
                            }}
                          >
                            <AppCard
                              title={entry.title!}
                              text={entry.integrationPrimaryCategory?.[0]?.title!}
                              image={
                                logo ? (
                                  <Image src={logo.url!} width={64} height={64} sizes="64px" alt={logo.altText ?? ''} />
                                ) : undefined
                              }
                            />
                          </a>
                        </Link>
                      );
                    })}
                  </AppGrid>
                )}

                {filteredApps.length === 0 && (
                  <NotFoundWrapper>
                    <Image src="/webassets/illustrations/not-found-man.svg" width={122} height={114} alt="" />
                    <p>
                      We couldn’t find any results matching your request.
                      <br />
                      Please try a different combination.
                    </p>
                  </NotFoundWrapper>
                )}
              </div>
            )}

            {(showHome || filteredApps.length !== entries?.length) && (
              <ButtonContainer>
                <ShowAllButtton
                  onClick={() => {
                    setSearchTerms(null);
                    setSelectedCategory('all');
                    setSelectedPlans(null);

                    gtm.track('click', {
                      element_type: 'link',
                      element_label: 'Show all integrations',
                      location: ANALYTICS_CATEGORY,
                    });
                  }}
                >
                  Show all integrations
                </ShowAllButtton>
              </ButtonContainer>
            )}
          </div>
        </Grid>

        <CalloutBlock>
          {/* TODO move this to CMS */}
          <Left>
            <Text>Don’t see your app? Partner with us to join the Platform.</Text>
          </Left>

          <Right>
            <StyledPrimaryDefaultButton
              as="a"
              href={INTEGRATIONS_PARTNER_INFO}
              target="_blank"
              onClick={() => {
                gtm.track('click', {
                  element_type: 'link',
                  element_label: 'Partner with us',
                  location: `${ANALYTICS_CATEGORY} | Footer`,
                });
              }}
            >
              Partner with us
            </StyledPrimaryDefaultButton>

            <StyledSecondaryDefaultButton
              as="a"
              href={INTEGRATIONS_DOCUMENTATION}
              target="_blank"
              onClick={() => {
                gtm.track('click', {
                  element_type: 'link',
                  element_label: 'View documentation',
                  location: `${ANALYTICS_CATEGORY} | Footer`,
                });
              }}
            >
              View documentation
            </StyledSecondaryDefaultButton>
          </Right>
        </CalloutBlock>
      </Container>
      <CTASection>
        <Container>
          <LargeCalloutBlock>
            <p>Join {CUSTOMER_COUNT} businesses working smarter with Front</p>

            <FrontPinkButton
              onClick={() => {
                toggleGetStartedModal(true);

                gtm.track('click', {
                  element_type: 'button',
                  element_label: 'Get Started',
                  location: `${ANALYTICS_CATEGORY} | Footer`,
                });
              }}
            >
              Get started
            </FrontPinkButton>
          </LargeCalloutBlock>
        </Container>
      </CTASection>
    </PageWrapper>
  );
};

IntegrationsIndex.template = MasterTemplate;

export default IntegrationsIndex;

export const getStaticProps = getStaticPageProps('integrations', async ({ client }) => {
  const props = await client.request<IntegrationsHomepageQuery>(IntegrationsHomepageQueryDocument);

  return { props };
});
